import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { selectClinicId, selectTherapistId } from "../../../store/slices/authSlice";
import { useGetActivityLogsQuery } from "../../../store/services/activityLogsApi";
import { useGetClinicTherapistsQuery } from "../../../store/services/therapistApi";
import { useGetTreatmentViewsQuery } from "../../../store/services/treatmentsApi";
import { CLINIC_ROLE_BADGES } from "../../../config/permissionsRegistry";
import { ACTION_LABELS, ENTITY_TYPES } from "../../../firebase";
import {
  ClockCounterClockwise,
  Funnel,
  User,
  Calendar,
  CaretLeft,
  CaretRight,
  UserPlus,
  CalendarCheck,
  ListChecks,
  NotePencil,
  Tag,
  Eye,
  PencilSimpleLine,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import DatePicker from "../../../components/ui/DatePicker";
import ContentLoader from "../../../components/shared/ContentLoader";
import LoaderLoop from "../../../components/shared/loader/LoaderLoop";
import "./AdminActivityLogsPage.css";

const PAGE_SIZE = 50;

// Tabs (admin only — hidden when `selfOnly`)
const TAB_LOGS = "logs";
const TAB_VIEWS = "views";

// treatmentViews.context → Hebrew label
const VIEW_CONTEXT_LABELS = {
  doc: "פתיחת דוח",
  profile: "כרטיס מטופל",
  report: "דוח PDF",
  ai: "סיכום AI",
};

// Action filter options
const ACTION_OPTIONS = [
  { value: "all", label: "כל הפעולות" },
  { value: "create_appointment", label: "קביעת תור" },
  { value: "update_appointment", label: "עדכון תור" },
  { value: "delete_appointment", label: "מחיקת תור" },
  { value: "create_patient", label: "יצירת מטופל" },
  { value: "update_patient", label: "עדכון מטופל" },
  { value: "delete_patient", label: "מחיקת מטופל" },
  { value: "create_task", label: "יצירת משימה" },
  { value: "update_task", label: "עדכון משימה" },
  { value: "delete_task", label: "מחיקת משימה" },
  { value: "create_reminder", label: "יצירת תזכורת" },
  { value: "update_reminder", label: "עדכון תזכורת" },
  { value: "delete_reminder", label: "מחיקת תזכורת" },
  { value: "create_category", label: "יצירת קטגוריה" },
  { value: "update_category", label: "עדכון קטגוריה" },
  { value: "delete_category", label: "מחיקת קטגוריה" },
  { value: "create_therapist", label: "הוספת איש/אשת צוות" },
  { value: "update_therapist_status", label: "שינוי סטטוס איש/אשת צוות" },
  // Multi-clinic v2 — team, sharing, absences and scheduling-policy waivers
  { value: "update_therapist_role", label: "עדכון תפקיד והרשאות" },
  { value: "transfer_patients", label: "העברת מטופלים בין מטפלים" },
  { value: "reassign_patient", label: "שיוך מטופל/ת למטפל/ת אחר/ת" },
  { value: "update_patient_sharing_mode", label: "שינוי מצב שיתוף מטופלים" },
  { value: "resend_team_invite", label: "שליחת תזכורת הצטרפות" },
  { value: "invite_accepted", label: "כניסה ראשונה למערכת" },
  { value: "clinic_upgraded_multi", label: "הקליניקה הפכה למרובת מטפלים" },
  { value: "create_absence", label: "הוספת היעדרות" },
  { value: "update_absence", label: "עדכון היעדרות" },
  { value: "delete_absence", label: "מחיקת היעדרות" },
  { value: "override_therapist_conflict", label: "קביעת תור חופף למטפל (עקיפה)" },
  { value: "override_absence", label: "קביעת תור בזמן היעדרות (עקיפה)" },
];

// Get icon for action type
const getActionIcon = (action) => {
  if (action.includes("appointment")) {
    return <CalendarCheck size={18} weight="fill" />;
  }
  if (action.includes("patient")) {
    return <UserPlus size={18} weight="fill" />;
  }
  if (action.includes("task")) {
    return <ListChecks size={18} weight="fill" />;
  }
  if (action.includes("reminder")) {
    return <NotePencil size={18} weight="fill" />;
  }
  if (action.includes("category")) {
    return <Tag size={18} weight="fill" />;
  }
  if (action.includes("therapist")) {
    return <User size={18} weight="fill" />;
  }
  return <ClockCounterClockwise size={18} weight="fill" />;
};

// Get action type class for styling
const getActionClass = (action) => {
  if (action.includes("create")) return "action-create";
  if (action.includes("update")) return "action-update";
  if (action.includes("delete")) return "action-delete";
  return "action-default";
};

// Format date to Hebrew display
const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Format date for input
const formatDateForInput = (date) => {
  if (!date) return "";
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * "צפיות בדוחות טיפול" — admin-only audit of cross-therapist treatment reads
 * (`/clinics/{id}/treatmentViews`). Only ONE equality filter may be active at a
 * time (viewerId | ownerTherapistId), so picking one clears the other.
 */
function TreatmentViewsTab({ clinicId, therapists }) {
  const [viewerId, setViewerId] = useState("all");
  const [ownerId, setOwnerId] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [cursors, setCursors] = useState([]);
  const [currentCursor, setCurrentCursor] = useState(null);

  const { data, isLoading, isFetching } = useGetTreatmentViewsQuery(
    {
      clinicId,
      viewerId: viewerId === "all" ? undefined : viewerId,
      ownerTherapistId: ownerId === "all" ? undefined : ownerId,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      cursor: currentCursor,
      pageSize: PAGE_SIZE,
    },
    { skip: !clinicId }
  );

  const views = data?.views || [];
  const hasMore = data?.hasMore || false;
  const nextCursor = data?.nextCursor;

  // therapistId → display name (no per-row queries)
  const therapistNames = useMemo(() => {
    const map = {};
    therapists.forEach((t) => {
      const id = t.uid || t.id;
      if (id) map[id] = `${t.firstName || ""} ${t.lastName || ""}`.trim();
    });
    return map;
  }, [therapists]);

  useEffect(() => {
    setCurrentPage(1);
    setCursors([]);
    setCurrentCursor(null);
  }, [viewerId, ownerId, startDate, endDate]);

  const handleViewerChange = (e) => {
    setViewerId(e.target.value);
    if (e.target.value !== "all") setOwnerId("all");
  };

  const handleOwnerChange = (e) => {
    setOwnerId(e.target.value);
    if (e.target.value !== "all") setViewerId("all");
  };

  const clearFilters = () => {
    setViewerId("all");
    setOwnerId("all");
    setStartDate("");
    setEndDate("");
  };

  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      setCursors([...cursors, currentCursor]);
      setCurrentCursor(nextCursor);
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newCursors = [...cursors];
      const prevCursor = newCursors.pop();
      setCursors(newCursors);
      setCurrentCursor(prevCursor);
      setCurrentPage(currentPage - 1);
    }
  };

  const hasActiveFilters = viewerId !== "all" || ownerId !== "all" || startDate || endDate;

  return (
    <>
      <p className="activity-logs-intro">
        כל צפייה בדוח טיפול של מטפל/ת אחר/ת נרשמת כאן. המחבר/ת אינו/ה מקבל/ת התראה.
      </p>

      <div className="activity-logs-filters">
        <div className="filter-wrapper">
          <Eye size={18} weight="regular" className="filter-icon" />
          <select value={viewerId} onChange={handleViewerChange} className="filter-select">
            <option value="all">כל הצופים</option>
            {therapists.map((t) => (
              <option key={t.uid || t.id} value={t.uid || t.id}>
                {t.firstName} {t.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-wrapper">
          <PencilSimpleLine size={18} weight="regular" className="filter-icon" />
          <select value={ownerId} onChange={handleOwnerChange} className="filter-select">
            <option value="all">כל המחברים</option>
            {therapists.map((t) => (
              <option key={t.uid || t.id} value={t.uid || t.id}>
                {t.firstName} {t.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-wrapper date-filter">
          <DatePicker
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="מתאריך"
          />
        </div>

        <div className="filter-wrapper date-filter">
          <DatePicker
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="עד תאריך"
          />
        </div>

        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={clearFilters}>
            נקה סינון
          </button>
        )}
      </div>

      {isLoading && (
        <ContentLoader message="טוען צפיות..." />
      )}

      {!isLoading && views.length === 0 && (
        <div className="empty-state">
          <Eye size={48} weight="light" />
          <h3>אין צפיות מתועדות</h3>
          <p>
            {hasActiveFilters
              ? "לא נמצאו צפיות התואמות את הסינון"
              : "עדיין לא נרשמו צפיות בדוחות טיפול של מטפלים אחרים"}
          </p>
        </div>
      )}

      {!isLoading && views.length > 0 && (
        <>
          <div className="activity-logs-table-wrapper">
            <table className="activity-logs-table">
              <thead>
                <tr>
                  <th>תאריך ושעה</th>
                  <th>מי צפה</th>
                  <th>מחבר/ת הדוח</th>
                  <th>מטופל/ת</th>
                  <th>הקשר</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {views.map((view) => (
                  <tr key={view.id}>
                    <td className="datetime-cell">{formatDateTime(view.viewedAt)}</td>
                    <td className="therapist-cell">
                      <div className="therapist-info">
                        <User size={16} weight="fill" />
                        <span>{view.viewerName || therapistNames[view.viewerId] || "—"}</span>
                        {CLINIC_ROLE_BADGES[view.viewerRole] && (
                          <span className="role-badge">{CLINIC_ROLE_BADGES[view.viewerRole]}</span>
                        )}
                      </div>
                    </td>
                    <td className="therapist-cell">
                      <div className="therapist-info">
                        <span>{therapistNames[view.ownerTherapistId] || "—"}</span>
                      </div>
                    </td>
                    <td>
                      {view.patientId ? (
                        <Link
                          to={`/dashboard/patients/${view.patientId}`}
                          className="view-link"
                          title={view.patientId}
                        >
                          מטופל/ת #{view.patientId.slice(0, 6)}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <span className="action-badge action-default">
                        {VIEW_CONTEXT_LABELS[view.context] || view.context || "—"}
                      </span>
                    </td>
                    <td>
                      {view.patientId && view.treatmentId && (
                        <Link
                          to={`/dashboard/patients/${view.patientId}/treatments/${view.treatmentId}`}
                          className="view-link view-link-strong"
                        >
                          <ArrowSquareOut size={14} weight="bold" />
                          <span>פתח דוח</span>
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1 || isFetching}
            >
              <CaretRight size={18} weight="bold" />
              <span>הקודם</span>
            </button>

            <span className="pagination-info">
              עמוד {currentPage}
              {isFetching && <LoaderLoop size="xs" />}
            </span>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={!hasMore || isFetching}
            >
              <span>הבא</span>
              <CaretLeft size={18} weight="bold" />
            </button>
          </div>
        </>
      )}
    </>
  );
}

/**
 * `selfOnly` (multi-clinic v2, route /dashboard/activity — "הפעילות שלי"):
 * every therapist may read their OWN rows (rules: therapistId == authUid()),
 * so the query is pinned to the signed-in therapist and the therapist filter
 * is hidden. Admin usage is unchanged.
 */
function AdminActivityLogsPage({ selfOnly = false }) {
  const clinicId = useSelector(selectClinicId);
  const myTherapistId = useSelector(selectTherapistId);

  // Tabs — the views tab exists only for admins (rules: only admins read treatmentViews)
  const [activeTab, setActiveTab] = useState(TAB_LOGS);
  const showViewsTab = !selfOnly && activeTab === TAB_VIEWS;

  // Filters
  const [selectedTherapist, setSelectedTherapist] = useState("all");
  const [selectedAction, setSelectedAction] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [cursors, setCursors] = useState([]); // Stack of cursors for previous pages
  const [currentCursor, setCurrentCursor] = useState(null);

  // Get therapists for filter
  const { data: therapists = [] } = useGetClinicTherapistsQuery(
    { clinicId },
    { skip: !clinicId || selfOnly }
  );

  // Get activity logs
  const { data, isLoading, isFetching, refetch } = useGetActivityLogsQuery(
    {
      clinicId,
      therapistId: selfOnly ? myTherapistId : selectedTherapist === "all" ? undefined : selectedTherapist,
      action: selectedAction === "all" ? undefined : selectedAction,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      cursor: currentCursor,
      pageSize: PAGE_SIZE,
    },
    { skip: !clinicId || (selfOnly && !myTherapistId) || showViewsTab }
  );

  const logs = data?.logs || [];
  const hasMore = data?.hasMore || false;
  const nextCursor = data?.nextCursor;

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setCursors([]);
    setCurrentCursor(null);
  }, [selectedTherapist, selectedAction, startDate, endDate]);

  // Handle therapist filter change
  const handleTherapistChange = (e) => {
    setSelectedTherapist(e.target.value);
  };

  // Handle action filter change
  const handleActionChange = (e) => {
    setSelectedAction(e.target.value);
  };

  // Handle date filter changes
  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedTherapist("all");
    setSelectedAction("all");
    setStartDate("");
    setEndDate("");
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (hasMore && nextCursor) {
      setCursors([...cursors, currentCursor]);
      setCurrentCursor(nextCursor);
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      const newCursors = [...cursors];
      const prevCursor = newCursors.pop();
      setCursors(newCursors);
      setCurrentCursor(prevCursor);
      setCurrentPage(currentPage - 1);
    }
  };

  // Check if any filters are active
  const hasActiveFilters = 
    selectedTherapist !== "all" || 
    selectedAction !== "all" || 
    startDate || 
    endDate;

  return (
    <div className="admin-activity-logs-page">
      <div className="activity-logs-header">
        <div className="header-title">
          <h1>
            <ClockCounterClockwise size={28} weight="bold" />
            <span>{selfOnly ? "הפעילות שלי" : "תיעודי פעילות"}</span>
          </h1>
          <p className="subtitle">
            {selfOnly
              ? "כל הפעולות שביצעת במערכת"
              : "צפייה בכל הפעולות שבוצעו במערכת על ידי המטפלים"}
          </p>
        </div>

      </div>

      {!selfOnly && (
        <div className="activity-logs-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_LOGS}
            className={`activity-logs-tab ${activeTab === TAB_LOGS ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_LOGS)}
          >
            <ClockCounterClockwise size={15} weight={activeTab === TAB_LOGS ? "fill" : "regular"} />
            <span>יומן פעילות</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_VIEWS}
            className={`activity-logs-tab ${activeTab === TAB_VIEWS ? "active" : ""}`}
            onClick={() => setActiveTab(TAB_VIEWS)}
          >
            <Eye size={15} weight={activeTab === TAB_VIEWS ? "fill" : "regular"} />
            <span>צפיות בדוחות טיפול</span>
          </button>
        </div>
      )}

      {showViewsTab && <TreatmentViewsTab clinicId={clinicId} therapists={therapists} />}

      {!showViewsTab && (
      <>
      <div className="activity-logs-filters">
        {/* Therapist Filter */}
        {!selfOnly && (
        <div className="filter-wrapper">
          <User size={18} weight="regular" className="filter-icon" />
          <select
            value={selectedTherapist}
            onChange={handleTherapistChange}
            className="filter-select"
          >
            <option value="all">כל המטפלים</option>
            {therapists.map((therapist) => (
              <option key={therapist.uid || therapist.id} value={therapist.uid || therapist.id}>
                {therapist.firstName} {therapist.lastName}
              </option>
            ))}
          </select>
        </div>
        )}

        {/* Action Filter */}
        <div className="filter-wrapper">
          <Funnel size={18} weight="regular" className="filter-icon" />
          <select
            value={selectedAction}
            onChange={handleActionChange}
            className="filter-select"
          >
            {ACTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Date Range Filters */}
        <div className="filter-wrapper date-filter">
          <DatePicker
            value={startDate}
            onChange={handleStartDateChange}
            placeholder="מתאריך"
          />
        </div>

        <div className="filter-wrapper date-filter">
          <DatePicker
            value={endDate}
            onChange={handleEndDateChange}
            placeholder="עד תאריך"
          />
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button className="clear-filters-btn" onClick={clearFilters}>
            נקה סינון
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <ContentLoader message="טוען תיעודי פעילות..." />
      )}

      {/* Empty State */}
      {!isLoading && logs.length === 0 && (
        <div className="empty-state">
          <ClockCounterClockwise size={48} weight="light" />
          <h3>אין תיעודי פעילות</h3>
          <p>
            {hasActiveFilters
              ? "לא נמצאו תיעודים התואמים את הסינון"
              : "עדיין לא בוצעו פעולות במערכת"}
          </p>
        </div>
      )}

      {/* Activity Logs Table */}
      {!isLoading && logs.length > 0 && (
        <>
          <div className="activity-logs-table-wrapper">
            <table className="activity-logs-table">
              <thead>
                <tr>
                  <th>תאריך ושעה</th>
                  <th>מטפל</th>
                  <th>פעולה</th>
                  <th>פרטים</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="datetime-cell">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="therapist-cell">
                      <div className="therapist-info">
                        <User size={16} weight="fill" />
                        <span>{log.therapistName}</span>
                      </div>
                    </td>
                    <td className="action-cell">
                      <div className={`action-badge ${getActionClass(log.action)}`}>
                        {getActionIcon(log.action)}
                        <span>{log.actionLabel || ACTION_LABELS[log.action] || log.action}</span>
                      </div>
                    </td>
                    <td className="details-cell">
                      <div className="details-content">
                        {log.entityName && (
                          <span className="entity-name">{log.entityName}</span>
                        )}
                        {log.details && Object.keys(log.details).length > 0 && (
                          <span className="details-extra">
                            {log.details.startTime && (
                              <span className="detail-item">
                                {new Date(log.details.startTime).toLocaleString("he-IL", {
                                  day: "numeric",
                                  month: "short",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            )}
                            {log.details.newStatus && (
                              <span className="detail-item status">
                                סטטוס: {log.details.newStatus}
                              </span>
                            )}
                            {log.details.scope && (
                              <span className="detail-item scope">
                                {log.details.scope === 'personal' ? 'אישי' : 'קליניקה'}
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1 || isFetching}
            >
              <CaretRight size={18} weight="bold" />
              <span>הקודם</span>
            </button>

            <span className="pagination-info">
              עמוד {currentPage}
              {isFetching && <LoaderLoop size="xs" />}
            </span>

            <button
              className="pagination-btn"
              onClick={handleNextPage}
              disabled={!hasMore || isFetching}
            >
              <span>הבא</span>
              <CaretLeft size={18} weight="bold" />
            </button>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
}

export default AdminActivityLogsPage;

